/**
 * R126 · Feb 2026 — HeaderMobileOverflow
 *
 * Small-screen overflow ("⋯") menu that houses the secondary header
 * widgets so the app header stays legible on phones. Only rendered
 * below the `sm` breakpoint via a Tailwind visibility toggle on the
 * caller side (`sm:hidden`).
 *
 * Items exposed:
 *   • Clock in / out
 *   • My compliance (goes to attendance)
 *   • Create → task / project / milestone / package
 *   • Daily plan popup
 *   • Timesheet quick-view
 *   • Jobs indicator
 *   • WhatsApp inbox shortcut
 *
 * Each row uses a plain-navigation approach — no in-place popovers,
 * just direct routes — so the overflow menu stays tap-friendly.
 */
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal, Plus, LogIn, CheckSquare, FolderKanban, Flag,
  Package as PackageIcon, Calendar, ShieldAlert, Clock, MessageSquare,
  Briefcase,
} from "lucide-react";

const CREATE_ITEMS = [
  { key: "task",      label: "Task",      icon: CheckSquare,  to: "/app/tasks?new=1" },
  { key: "project",   label: "Project",   icon: FolderKanban, to: "/app/projects?new=1" },
  { key: "milestone", label: "Milestone", icon: Flag,         to: "/app/projects" },
  { key: "package",   label: "Package",   icon: PackageIcon,  to: "/app/packages?new=1" },
];

export default function HeaderMobileOverflow() {
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="h-8 w-8 flex items-center justify-center hover:bg-muted/60"
          title="More"
          data-testid="header-mobile-overflow-btn"
          aria-label="More header actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-foreground/50">
          Quick actions
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => nav("/app/attendance")}
                          data-testid="header-mobile-overflow-clock">
          <LogIn className="w-4 h-4 mr-2 text-emerald-500" /> Clock in / out
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/app/me?tab=compliance")}
                          data-testid="header-mobile-overflow-compliance">
          <ShieldAlert className="w-4 h-4 mr-2" /> My compliance
        </DropdownMenuItem>
        {/* R138j · Feb 2026 — Timesheet is now a persistent header pill on
            mobile too, so we remove the overflow duplicate. */}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-foreground/50">
          Create new
        </DropdownMenuLabel>
        {CREATE_ITEMS.map((it) => {
          const Icon = it.icon;
          return (
            <DropdownMenuItem
              key={it.key}
              onClick={() => nav(it.to)}
              data-testid={`header-mobile-overflow-create-${it.key}`}
            >
              <Plus className="w-3 h-3 mr-1 text-foreground/40" />
              <Icon className="w-4 h-4 mr-2 text-primary" /> {it.label}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("open-daily-plan"))}
                          data-testid="header-mobile-overflow-plan">
          <Calendar className="w-4 h-4 mr-2" /> Daily plan
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/app/jobs")}
                          data-testid="header-mobile-overflow-jobs">
          <Briefcase className="w-4 h-4 mr-2" /> Jobs
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/app/whatsapp")}
                          data-testid="header-mobile-overflow-wa">
          <MessageSquare className="w-4 h-4 mr-2 text-emerald-600" /> WhatsApp inbox
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
